'use client';

import { Close, Content, Overlay, Portal, Root, Title } from '@radix-ui/react-dialog';

interface ModalButton {
  onClick: () => void;
  title: string;
  variant?: string;
  dataCy?: string;
}

interface UnifiedModalProps {
  className?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  firstButton?: ModalButton;
  secondButton?: ModalButton;
}

export const UnifiedModal = ({
  className = '',
  isOpen,
  onOpenChange,
  title,
  children,
  firstButton,
  secondButton,
}: UnifiedModalProps) => {
  return (
    <Root
      onOpenChange={onOpenChange}
      open={isOpen}
    >
      <Portal>
        <Overlay className='fixed inset-0 bg-black/50 z-[100000]' />

        <div className='fixed inset-0 z-[100001] flex items-center justify-center pointer-events-none'>
          <Content
            aria-describedby='unified-modal-desc'
            className={`pointer-events-auto bg-white rounded-lg shadow-xl border border-gray-200 w-[400px] max-w-[90vw] focus:outline-none ${className}`}
          >
            <Title className='sr-only'>{title}</Title>
            <div className='flex items-center justify-between p-6 border-b border-gray-200'>
              <div
                aria-hidden='true'
                className='text-lg font-semibold text-gray-900'
              >
                {title}
              </div>
              <Close
                aria-label='Close'
                className='h-6 w-6 text-gray-400 hover:text-gray-600 cursor-pointer'
              >
                <span aria-hidden='true'>&times;</span>
              </Close>
            </div>

            <div
              className='p-6 space-y-4'
              id='unified-modal-desc'
            >
              {children}

              <div className='flex gap-3 pt-4'>
                {firstButton && (
                  <button
                    className='flex-1 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 font-medium'
                    data-cy={firstButton.dataCy}
                    onClick={firstButton.onClick}
                    type='button'
                  >
                    {firstButton.title}
                  </button>
                )}
                {secondButton && (
                  <button
                    className='flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 font-medium'
                    data-cy={secondButton.dataCy}
                    onClick={secondButton.onClick}
                    type='button'
                  >
                    {secondButton.title}
                  </button>
                )}
              </div>
            </div>
          </Content>
        </div>
      </Portal>
    </Root>
  );
};

// named export only
